import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, BarChart2, TrendingUp, Lightbulb, ClipboardList, Users, LineChart } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { DjayAvatar, DjayBubble } from '../../../mascot';
import type { AccountRole } from '../../types';
import { OnboardingProgress } from './OnboardingProgress';
interface OnboardingWelcomeStepProps {
  role: AccountRole;
  name: string;
  groupLevel?: number;
  onNext: () => void;
  onGroupChange?: (group: number) => void;
}
export function OnboardingWelcomeStep({
  role,
  name,
  groupLevel = 6,
  onNext,
  onGroupChange
}: OnboardingWelcomeStepProps) {
  const getRoleConfig = () => {
    switch (role) {
      case 'learner':
        return {
          emotion: 'excited' as const,
          message: `Welkom ${name}! Ik ben Djay, jouw leermaatje. Samen gaan we oefenen en leren!`,
          gradient: 'from-orange-400 to-amber-400',
          border: 'border-orange-100',
          buttonBg: 'bg-orange-500 hover:bg-orange-600'
        };
      case 'parent':
        return {
          emotion: 'happy' as const,
          message: 'Welkom! Hier kunt u de voortgang van uw kind volgen.',
          gradient: 'from-emerald-400 to-teal-400',
          border: 'border-emerald-100',
          buttonBg: 'bg-emerald-600 hover:bg-emerald-700'
        };
      case 'teacher':
        return {
          emotion: 'celebrating' as const,
          message: 'Welkom! Laten we uw klas klaarzetten.',
          gradient: 'from-indigo-500 to-blue-400',
          border: 'border-indigo-100',
          buttonBg: 'bg-indigo-600 hover:bg-indigo-700'
        };
      default:
        return {
          emotion: 'happy' as const,
          message: 'Welkom!',
          gradient: 'from-slate-400 to-slate-500',
          border: 'border-slate-100',
          buttonBg: 'bg-slate-600 hover:bg-slate-700'
        };
    }
  };
  const config = getRoleConfig();
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="w-full max-w-md mx-auto" data-id="element-300">
      
      {/* Mascot Section */}
      <div className="flex flex-col items-center mb-6 relative" data-id="element-301">
        <motion.div animate={{
        y: [0, -8, 0]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-302">
          
          <DjayAvatar emotion={config.emotion} size="lg" data-id="element-303" />
        </motion.div>
        <div className="mt-3 w-full max-w-[260px]" data-id="element-304">
          <DjayBubble message={config.message} position="top" colorScheme={role === 'learner' ? 'amber' : role === 'parent' ? 'emerald' : role === 'teacher' ? 'indigo' : 'slate'} autoDismissMs={0} data-id="element-305" />
          
        </div>
      </div>

      {/* Card */}
      <div className={`bg-white rounded-3xl p-6 sm:p-8 shadow-lg border ${config.border} relative overflow-hidden`} data-id="element-306">
        
        <div className={`absolute top-0 left-0 w-full h-2 bg-gradient-to-r ${config.gradient}`} data-id="element-307" />
        

        <OnboardingProgress currentStep={0} totalSteps={role === 'learner' ? 2 : 3} role={role} data-id="element-308" />
        

        {role === 'learner' && <div className="space-y-6" data-id="element-309">
            <div className="text-center" data-id="element-310">
              <h2 className="text-2xl font-black text-gray-900 mb-2" data-id="element-311">
                Klaar voor de start? 🚀
              </h2>
              <p className="text-gray-600 font-medium" data-id="element-312">
                Je gaat oefenen op het niveau van{' '}
                <span className="font-bold text-orange-600" data-id="element-313">
                  groep {groupLevel}
                </span>
                .
              </p>
            </div>

            <div className="bg-orange-50 border border-orange-100 rounded-2xl p-5 space-y-4" data-id="element-314">
              <motion.div className="flex items-center gap-3" initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.1
          }} data-id="element-315">
              
                <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-orange-500 flex-shrink-0" data-id="element-316">
                  <span className="text-xl" data-id="element-317">🎯</span>
                </div>
                <p className="text-sm font-medium text-orange-900" data-id="element-318">
                  Oefen rekenen, taal en meer op jouw niveau
                </p>
              </motion.div>
              <motion.div className="flex items-center gap-3" initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.2
          }} data-id="element-319">
              
                <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-orange-500 flex-shrink-0" data-id="element-320">
                  <span className="text-xl" data-id="element-321">⭐</span>
                </div>
                <p className="text-sm font-medium text-orange-900" data-id="element-322">
                  Verdien XP en ontgrendel nieuwe titels
                </p>
              </motion.div>
              <motion.div className="flex items-center gap-3" initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.3
          }} data-id="element-323">
              
                <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-orange-500 flex-shrink-0" data-id="element-324">
                  <span className="text-xl" data-id="element-325">🔥</span>
                </div>
                <p className="text-sm font-medium text-orange-900" data-id="element-326">
                  Bouw een streak op door elke dag te oefenen
                </p>
              </motion.div>
            </div>
          </div>}

        {role === 'parent' && <div className="space-y-6" data-id="element-327">
            <h2 className="text-xl font-bold text-center text-gray-900" data-id="element-328">
              Wat kunt u hier doen?
            </h2>
            <ul className="space-y-4" data-id="element-329">
              <li className="flex items-center gap-3 text-gray-700" data-id="element-330">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-331">
                  <BarChart2 className="w-5 h-5 text-emerald-600" data-id="element-332" />
                </div>
                <span data-id="element-333">Voortgang per vak volgen</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-334">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-335">
                  <TrendingUp className="w-5 h-5 text-emerald-600" data-id="element-336" />
                </div>
                <span data-id="element-337">Groei over tijd bekijken</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-338">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-339">
                  <Lightbulb className="w-5 h-5 text-emerald-600" data-id="element-340" />
                </div>
                <span data-id="element-341">Persoonlijke oefentips</span>
              </li>
            </ul>
          </div>}

        {role === 'teacher' && <div className="space-y-6" data-id="element-342">
            <h2 className="text-xl font-bold text-center text-gray-900" data-id="element-343">
              Wat kunt u hier doen?
            </h2>
            <ul className="space-y-4" data-id="element-344">
              <li className="flex items-center gap-3 text-gray-700" data-id="element-345">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-346">
                  <ClipboardList className="w-5 h-5 text-indigo-600" data-id="element-347" />
                </div>
                <span data-id="element-348">Dagelijks klas-overzicht</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-349">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-350">
                  <Users className="w-5 h-5 text-indigo-600" data-id="element-351" />
                </div>
                <span data-id="element-352">Leerlingen beheren</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-353">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-354">
                  <LineChart className="w-5 h-5 text-indigo-600" data-id="element-355" />
                </div>
                <span data-id="element-356">Groei en trends volgen</span>
              </li>
            </ul>
          </div>}

        <div className="mt-8" data-id="element-357">
          <Button onClick={onNext} className={`w-full py-6 text-lg text-white ${config.buttonBg}`} data-id="element-358">
            
            {role === 'learner' ? 'Laten we beginnen!' : 'Verder'}
            <ArrowRight className="w-5 h-5 ml-2" data-id="element-359" />
          </Button>
        </div>
      </div>
    </motion.div>;
}